import React, { useState, useEffect } from 'react';
import { Card, Alert, Collapse, Button } from 'react-bootstrap';
import LoadingSpinner from '../common/LoadingSpinner';
import { searchService } from '../../services/searchService';
import ServiceAgreementPanel from './ServiceAgreementPanel';

/**
 * Collapsible section that displays usage history for the current customer.
 * Fetches data on mount and displays ServiceAgreementPanel(s).
 */
const UsageHistorySection = ({ accountId }) => {
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    const loadUsageHistory = async () => {
      if (!accountId) {
        setLoading(false);
        return;
      }

      try {
        const response = await searchService.getUsageHistory();
        if (response.success) {
          setUsageData(response);
        } else {
          setError(response.error || 'Failed to load usage history.');
        }
      } catch (err) {
        console.error('Error loading usage history:', err);
        setError('An error occurred while loading usage history.');
      } finally {
        setLoading(false);
      }
    };

    loadUsageHistory();
  }, [accountId]);

  if (loading) {
    return (
      <Card className="shadow mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Usage History</h5>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <LoadingSpinner inline message="Loading usage history..." />
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow mb-4">
        <Card.Header>
          <h5 className="mb-0">Usage History</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="danger" className="mb-0">
            {error}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  const serviceAgreements = usageData?.serviceAgreements || [];

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Usage History</h5>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
        >
          {isOpen ? 'Collapse' : 'Expand'}
        </Button>
      </div>

      <Collapse in={isOpen}>
        <div>
          {serviceAgreements.length === 0 ? (
            <Alert variant="info">
              No usage history found for this customer.
            </Alert>
          ) : (
            serviceAgreements.map((sa, index) => (
              <ServiceAgreementPanel
                key={sa.id || index}
                serviceAgreement={sa}
                accountId={accountId}
              />
            ))
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default UsageHistorySection;